import moment from 'moment';

/**
 * Turns a meter's alert instances into a readable history.
 *
 * The alerts are already stored against the meter; nothing surfaced them, so
 * "why was last month low?" had no answer on the page that raises the question.
 */

/** Statuses the API returns for an alert instance. */
export const AlertStatus = { OPEN: 'OPEN', RESOLVED: 'RESOLVED' };

/**
 * Whether an alert is still open.
 *
 * The status decides it. A missing `closedAt` does not: thirteen of the
 * ninety-two instances on this fleet are RESOLVED with no closing timestamp —
 * writes from January and February that did not finish — and treating those as
 * open put four healthy meters in the "still alerting" column and left them
 * with no reporting-since figure.
 *
 * A row carrying no status at all falls back to the timestamp, which is the
 * only thing left to go on.
 *
 * @param {object} instance - Alert instance as returned by the API.
 * @returns {boolean} True while the alert has not been resolved.
 */
export const isOpen = (instance) =>
  instance?.status
    ? instance.status === AlertStatus.OPEN
    : !instance?.closedAt;

/**
 * Describes how long an outage lasted, in the largest unit that reads well.
 * @param {string} from - When the alert opened.
 * @param {string} to - When it closed, or nothing while it is still open.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {string} A phrase such as '7.6 hours' or '3 days'.
 */
export const outageLength = (from, to, now = () => moment()) => {
  const opened = from ? moment(from) : null;
  if (!opened?.isValid()) return 'unknown';

  const closed = to ? moment(to) : now();
  const hours = closed.diff(opened, 'hours', true);

  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${Math.round(hours * 10) / 10} hours`;
  return `${Math.round(hours / 24)} days`;
};

/**
 * Reads an alert instance into the fields the timeline shows.
 * @param {object} instance - Alert instance as returned by the API.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {object|null} Row for the timeline, or null when unusable.
 */
export const describeAlert = (instance, now = () => moment()) => {
  if (!instance?.openedAt) return null;

  const open = isOpen(instance);

  return {
    id: instance.id,
    openedAt: instance.openedAt,
    when: moment(instance.openedAt).format('DD MMM YYYY, HH:mm'),
    length: outageLength(instance.openedAt, instance.closedAt, now),
    readingState: instance.meterReadingState || null,
    resolvedAt: instance.closedAt ? moment(instance.closedAt).format('DD MMM, HH:mm') : null,
    open,
    tone: open ? 'bad' : 'warn',
    label: open ? 'Open' : 'Resolved',
  };
};

/**
 * When a meter last returned to normal, or null while it has not.
 *
 * This is how long a meter has been up in the sense a reader means it: no
 * outage has been raised against it since. The flow status has an
 * `activeSince`, but the job maintaining it restarts the run whenever a
 * gateway misses a beat, so on a fleet reporting every few seconds it measures
 * the gap since the last hiccup rather than the length of the run.
 *
 * A meter with an alert still open has not returned to normal, so there is
 * nothing to date from — the same reason the timeline shows no settled row.
 *
 * @param {Array<object>} instances - Alert instances for one meter.
 * @returns {string|null} When the last alert closed, or null.
 */
export const lastSettledAt = (instances = []) => {
  const rows = Array.isArray(instances) ? instances : [];

  if (!rows.length || rows.some(isOpen)) return null;

  return rows.map((instance) => instance.closedAt).filter(Boolean).sort().pop() || null;
};

/**
 * Groups alert instances by the meter they were raised against.
 *
 * The list fetches every meter's history in one request rather than one per
 * card, so the grid needs them split back out.
 *
 * @param {Array<object>} instances - Alert instances across all meters.
 * @returns {object} Meter id to its instances.
 */
export const groupAlertsByMeter = (instances = []) =>
  (Array.isArray(instances) ? instances : []).reduce((byMeter, instance) => {
    if (!instance?.meterId) return byMeter;

    byMeter[instance.meterId] = byMeter[instance.meterId] || [];
    byMeter[instance.meterId].push(instance);

    return byMeter;
  }, {});

/**
 * Builds the timeline, newest first, with a closing row for the quiet stretch
 * since the last alert.
 *
 * @param {Array<object>} instances - Alert instances for one meter.
 * @param {Function} now - Clock, injectable so tests stay deterministic.
 * @returns {{rows: Array<object>, openCount: number}} Timeline rows.
 */
export const buildTimeline = (instances = [], now = () => moment()) => {
  const rows = (Array.isArray(instances) ? instances : [])
    .map((instance) => describeAlert(instance, now))
    .filter(Boolean)
    .sort((a, b) => new Date(b.openedAt) - new Date(a.openedAt));

  const openCount = rows.filter((row) => row.open).length;

  if (rows.length && !openCount) {
    const latest = rows[0];
    const since = lastSettledAt(instances);

    if (since) {
      rows.unshift({
        id: 'settled',
        when: `Since ${moment(since).format('DD MMM')}`,
        length: outageLength(since, null, now),
        tone: 'ok',
        label: 'Healthy',
        settled: true,
        latestId: latest.id,
      });
    }
  }

  return { rows, openCount };
};
